import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Slide } from 'react-swift-reveal';
import Lottie from "lottie-react";
import HomeworkCompletedAnimation from "assets/images/HomeworkCompletedAnimation.json";
import HomeworkIncompletedAnimation from "assets/images/HomeworkIncompletedAnimation.json";
import { API_HOST } from 'utils';

const HomeworkTrack: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string>("");
  const [selectedRemark, setSelectedRemark] = useState<string>('');
  const [otherDesc, setOtherDesc] = useState<string>('');

  // Get id and status from URL params
  const id = searchParams.get('id');
  const status = searchParams.get('status');

  // Define remark options
  const remarkOptions = [
    'Health Issue',
    'Family Emergency',
    'Technical Problem',
    'Incomplete understanding of homework',
    'Need More Time',
    'Others'
  ];

  // Handle API call for both completed and incomplete status
  const handleAPICall = async (statusFromUrl?: string) => {
    if (!id) {
      setError('Missing ID parameter');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Determine if status is completed (case-insensitive check)
      const isCompleted = statusFromUrl?.toLowerCase() === 'completed';

      const payload = {
        id,
        PARENT_STATUS: isCompleted ? "COMPLETED" : "INCOMPLETE",
        PARENT_REMARK: isCompleted ? "" : (selectedRemark === "Others" ? `${selectedRemark} : ${otherDesc}` : selectedRemark)
      };

      const response = await axios.post(`${API_HOST}/data/parentHomework/update`, payload, {
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (response.data && [200,201].includes(response?.data?.code)) {
        setSuccess(success => (isCompleted ? "COMPLETED" : "INCOMPLETE"));
      } else {
        setError('Failed to update homework status');
      }
    } catch (err) {
      setError('An error occurred while updating homework status');
      console.error('API Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission for incomplete status
  const handleSubmit = async () => {
    await handleAPICall();
  };

  // Check if status is "completed" from URL param and hit API
  useEffect(() => {
    if (status && status.toLowerCase() === 'completed') {
      handleAPICall(status);
    }
  }, [status, id]);

  const handleOnSelect = (remark) => {
    setSelectedRemark(selectedRemark => remark);
    setOtherDesc(otherDesc => "");
  }

  if (!id) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p>Missing ID parameter. Please check the URL.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center">
      <div className="flex flex-col items-center bg-white rounded-lg p-6 w-full max-w-lg">
        {loading ? (
          <div className="flex flex-col items-center text-center py-4">
            <p>Processing your request...</p>
          </div>
        ) : ["COMPLETED", "INCOMPLETE"]?.includes(success) ? (
          <div className="flex flex-col items-center gap-8">
            <Lottie
              animationData={success === "COMPLETED" ? HomeworkCompletedAnimation : HomeworkIncompletedAnimation}
              loop={false}
            />
            <div>
              <Slide bottom duration={800} delay={300}>
                <h1 className='font-semibold text-center text-[1rem]'>Homework Status Submitted Successfully</h1>
              </Slide>
            </div>
          </div>
        ) : (
          <>
            {status?.toLowerCase() !== 'completed' && (
              <>
                <div className='space-y-4'>
                  <p className="font-semibold text-lg">Please select a reason for incomplete homework*</p>
                  <div className="space-y-2 p-2 rounded-xl bg-[#F5F5F5]">
                    {remarkOptions.map((remark, index) => (
                      <>
                        <div key={remark} className="flex items-center p-2">
                          <input
                            type="radio"
                            id={remark}
                            name="remark"
                            value={remark}
                            checked={selectedRemark === remark}
                            onChange={() => handleOnSelect(remark)}
                            className="mr-2 text-lg font-normal bg-[#DADADA]"
                          />
                          <label htmlFor={remark} className='font-normal text-[#202020]'>{remark}</label>
                        </div>
                        {index !== (remarkOptions?.length - 1) && <hr />}
                        {Boolean(selectedRemark === "Others" && remark === "Others") && <>
                          <textarea onChange={e => Boolean(e.target.value?.length > 180) ? {} : setOtherDesc(e.target.value)} value={otherDesc} rows={9} className='w-full p-2 border border-[gray] rounded-xl' placeholder='Describe your reason here'>

                          </textarea>
                          <p className="text-sm text-right">
                            {180 - otherDesc?.length}/180 characters left
                          </p>
                        </>}

                      </>
                    ))}
                    <div>
                    </div>
                  </div>
                  <button
                    className={`mx-auto w-full font-inter text-[2.5vh] font-light bg-smd_blue-normal text-gray-100 p-3 rounded-xl hover:bg-smd_blue-dark transition-colors duration-300`}
                    disabled={!selectedRemark || loading || (selectedRemark === "Others" && !otherDesc)}
                    type='button'
                    onClick={handleSubmit}
                  >
                    {selectedRemark === "Others" ? "Submit" : "Done"}
                  </button>
                </div>
              </>
            )}

            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mt-4">
                <p>{error}</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default HomeworkTrack;